/* eslint-disable react-hooks/exhaustive-deps */
import { FunctionComponent as F, useEffect, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logoutAsync, removeUserAsync } from "../../core/store/user/user.thunks";
import { IState } from "../../core/store/store";
import { useAppDispatch } from "../../core/store/hooks";
import PersonIcon from '@mui/icons-material/Person';
import PasswordIcon from '@mui/icons-material/Password';
import LogoutIcon from '@mui/icons-material/Logout';
import DeleteIcon from '@mui/icons-material/Delete';
import { CHANGE_PASSWORD, CHANGE_USERNAME, LOGIN, HOME } from "../../core/router/paths";
import PageHeadline from "../../components/UI/Text/PageHeadline";
import Button from '../../components/UI/Button/Button';
import CountrySelect from '../../components/Account/CountrySelect';
import OrderReminder from '../../components/Account/OrderReminder';
import { validateEmail, validateSalesTax } from '../../utils/validate';
import { updateEmail, updateSalesTax } from "../../core/http/user";
import ChangePassword from "./ChangePassword";
import ChangeUsername from "./ChangeUsername";
import HelpIcon from '@mui/icons-material/Help';
import TextTooltip from "../../components/UI/Tooltip/TextTooltip";
import {
  Container,
  Box,
  Stack,
  TextField,
  InputLabel,
  InputAdornment,
  OutlinedInput,
  Link,
  Typography,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup } from "@mui/material";
import PriceDropAlerts from "../../components/Account/PriceDropAlerts";
import { updateOrderReminder } from "../../core/store/user/user.slice";
import { postOrderReminder } from "../../core/http/user";
import { updateOrderReminder as updateProtocolOrderReminder } from "../../core/store/protocol/protocol.slice";
import { postOrderReminderActive as postProtocolOrderReminderActive } from "../../core/http/protocol";
import DeleteWarningDialog from "../../components/Account/DeleteWarningDialog";


const Account: F = () =>
{
  const dispatch = useDispatch();
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useSelector((state: IState) => state.user);
  const userProtocols = useSelector((state: IState) => state.protocol.protocols)
    .filter(p => p.userId === user.userId)

  const [prevEmail, setPrevEmail] = useState<string>(user.email || "");
  const [email, setEmail] = useState<string>(user.email || "");
  const [emailError, setEmailError] = useState<boolean>(false);
  const [emailMsg, setEmailMsg] = useState("");
  const [prevSalesTax, setPrevSalesTax] = useState<string>(String(user.salesTax));
  const [salesTax, setSalesTax] = useState<string>(String(user.salesTax));
  const [salesTaxError, setSalesTaxError] = useState<boolean>(false);
  const [salesTaxMsg, setSalesTaxMsg] = useState("");
  const [warningDialog, setWarningDialog] = useState(false);
  const [orderReminderChecked, setOrderReminderChecked] = useState<boolean>(Boolean(user.orderReminder.active));

  useEffect(() =>
  {
    if(!user.username)
      return navigate(LOGIN);
  }, []);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) =>
  {
    setEmail(event.target.value);
    setEmailError(!validateEmail(event.target.value));
  };

  const handleEmailBlur = async() =>
  {
    if(email === prevEmail)
      return;

    if(!validateEmail(email))
    {
      setEmail(prevEmail);
      setEmailError(false);
      return;
    }

    updateEmail(email)
      .then(data =>
      {
        if(data.emailExists)
        {
          setEmailError(true);
          setEmailMsg("An account with this email already exists.");
        }
        else if(data.success)
        {
          setEmailMsg("Email successfully changed.");
          setPrevEmail(email);
        }
        else if(!data.success)
        {
          setEmailMsg("Error updating email");
        }
      })
      .catch(err => setEmailMsg("Error updating email " + err));

  };

  const handleSalesTaxChange = (event: React.ChangeEvent<HTMLInputElement>) =>
  {
    setSalesTax(event.target.value);
    setSalesTaxError(!validateSalesTax(event.target.value));
  };

  const handleSalesTaxBlur = async() =>
  {
    if(salesTax === prevSalesTax)
      return;

    if(!validateSalesTax(salesTax))
    {
      setSalesTax(prevSalesTax);
      setSalesTaxError(false);
      return;
    }

    updateSalesTax(Number(salesTax))
      .then(data =>
      {
        if(!data.success)
          return setEmailMsg("Error updating sales tax");

        setSalesTax(Number(salesTax).toFixed(1));
        setSalesTaxMsg("Sales tax successfully changed.");
        setPrevSalesTax(salesTax);
      })
      .catch(err => setSalesTaxMsg("Error updating sales tax " + err));
  };

  async function handleLogout()
  {
    appDispatch(logoutAsync())
      .then(() => navigate(LOGIN));
  }

  async function handleDelete()
  {
    setWarningDialog(true);
  }

  const handleOrderReminderCheckboxClick = (event: React.MouseEvent) =>
  {
    // Require email address to activate order reminders
    if(user.email)
      return;

    event.preventDefault();
    setEmailError(true);
  }

  const handleOrderReminderCheckboxChange = async(
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean) =>
  {
    if(!user.email)
      return;

    dispatch(updateOrderReminder({ active: checked }));
    setOrderReminderChecked(checked);
    postOrderReminder({ active: checked });

    // If turning on order reminder, ensure at least one protocol order reminder is active
    if(!checked || userProtocols.filter(p => p.orderReminderActive).length)
      return;

    for(const protocolId of userProtocols.map(p => p.protocolId))
    {
      dispatch(updateProtocolOrderReminder({ protocolId, orderReminder: true }));
      await postProtocolOrderReminderActive(protocolId, true);
    }
  };

  const avalaraUrl = "https://www.avalara.com/taxrates/en/calculator.html";

  return(
    <Container>

      <Stack sx={{ pb: 10 }} spacing={2}>
        <PageHeadline>Account</PageHeadline>

        {/* TODO: create input component with labels */}
        <Typography>Location</Typography>
        <CountrySelect/>

        <Box sx={{ height: 0.02 }}/>

        <TextField
          size='small'
          label='Email'
          value={email}
          onChange={handleEmailChange}
          onBlur={handleEmailBlur}
          error={emailError}
          style={{ maxWidth: 300, marginTop: 4 }}/>
        <Typography variant='body2' color='gray'><i>{emailMsg}</i></Typography>

        {user.countryId === 2 ?
          <>
          <Stack direction='row' spacing={1}>
            <FormControl>
              <InputLabel htmlFor='outlined-adornment-amount'>
                Sales Tax
              </InputLabel>
              <OutlinedInput
                size='small'
                sx={{ width: '243px' }}
                endAdornment={<InputAdornment position='end'>%</InputAdornment>}
                id='SalesTax'
                label='Sales Tax'
                value={salesTax}
                onChange={handleSalesTaxChange}
                onBlur={handleSalesTaxBlur}
                error={salesTaxError}/>
            </FormControl>
            <TextTooltip tooltipPlacement={"top"} lines={["Find yours here:", avalaraUrl]}>
              <Link
                variant='body2'
                target='_blank'
                rel='noreferrer'
                href={avalaraUrl} >
                  <HelpIcon
                    cursor="pointer"
                    sx={{ height: 20, color: 'lightGray', cursor: 'pointer', pt: 1 }}/>
                </Link>
            </TextTooltip>
            </Stack>
            <Typography variant='body2' color='gray'><i>{salesTaxMsg}</i></Typography>
          </> :
          <></>}

        {user.premium ?
          <>
            <FormGroup sx={{ width: '330px' }}>
              <FormControlLabel control={<Checkbox
                checked={orderReminderChecked}
                onClick={handleOrderReminderCheckboxClick}
                onChange={handleOrderReminderCheckboxChange}
                inputProps={{ 'aria-label': 'controlled' }}/>} label="Email reminder for supplement order" />
            </FormGroup>
            <OrderReminder/>
          </>:
          <></>}

        {user.premium ? <PriceDropAlerts/> : <></>}

        <hr style={{ marginTop: 50, marginBottom: 20, borderColor: 'white' }} />

        <Button
          startIcon={<PersonIcon />}
          onClick={() => navigate(CHANGE_USERNAME)}
          label='Change username'
          size='medium'/>
        <Button
          startIcon={<PasswordIcon />}
          onClick={() => navigate(CHANGE_PASSWORD)}
          label='Change password'
          size='medium'/>

      </Stack>

      <div style={{display: 'flex', justifyContent: 'space-between'}}>
        <Button
          id='Logout'
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          label='Log out'
          size='medium'/>
        <Button
          startIcon={<DeleteIcon />}
          onClick={handleDelete}
          id='Delete'
          label='Delete account'
          size='medium'
          type='secondary'/>
      </div>

      {warningDialog ?
        <DeleteWarningDialog close={() => setWarningDialog(false)} /> :
        <></>}

      <Routes>
        <Route path={CHANGE_USERNAME} element={<ChangeUsername/>}/>
        <Route path={CHANGE_PASSWORD} element={<ChangePassword/>}/>
      </Routes>

    </Container>);
};

export default Account;
